import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { sessionApi } from '@/api/session.api';
import type {
  SessionFeatureFlags,
  SessionHrMasters,
  SessionInitResponse,
  SessionReferenceData,
  SessionSettings,
} from '@/types/session';
import type { UiMenuGroup, UiScreenActions } from '@/types/ui';
import { filterHiddenNavItems } from '@/config/navHiddenRoutes';
import type { CompanyResponse } from '@/types/company';
import type { StoreResponse } from '@/types/store';
import type { RoleResponse } from '@/types/role';
import type { HrOrgStructure } from '@/types/hr';
import type { RootState } from '@/store';

import { hrMastersToOrgStructure as toOrgStructure } from '@/types/session';

interface SessionState {
  loaded: boolean;
  loading: boolean;
  activeCompanyId: number | null;
  companies: CompanyResponse[];
  stores: StoreResponse[];
  roles: RoleResponse[];
  referenceData: SessionReferenceData | null;
  hr: SessionHrMasters | null;
  orgStructure: HrOrgStructure | null;
  cacheHints: Record<string, number>;
  generatedAt: string | null;
  navigation: UiMenuGroup[];
  screenActions: Record<string, UiScreenActions>;
  settings: SessionSettings | null;
  featureFlags: SessionFeatureFlags | null;
}

const initialState: SessionState = {
  loaded: false,
  loading: false,
  activeCompanyId: null,
  companies: [],
  stores: [],
  roles: [],
  referenceData: null,
  hr: null,
  orgStructure: null,
  cacheHints: {},
  generatedAt: null,
  navigation: [],
  screenActions: {},
  settings: null,
  featureFlags: null,
};

function applySessionPayload(state: SessionState, data: SessionInitResponse) {
  state.loaded = true;
  state.companies = data.companyScope?.companies ?? [];
  state.activeCompanyId = data.companyScope?.activeCompanyId ?? null;
  state.stores = data.stores ?? [];
  state.roles = data.roles ?? [];
  state.referenceData = data.referenceData ?? null;
  state.hr = data.hr ?? null;
  state.orgStructure = toOrgStructure(data.hr);
  state.cacheHints = data.cacheHints ?? {};
  state.generatedAt = data.generatedAt ?? null;
  state.navigation = (data.menu?.navigation ?? [])
    .map((group) => ({
      ...group,
      items: filterHiddenNavItems(group.items ?? []),
    }))
    .filter((group) => (group.items?.length ?? 0) > 0);
  state.screenActions = data.menu?.screenActions ?? {};
  state.settings = data.settings ?? null;
  state.featureFlags = data.featureFlags ?? null;
}

export const bootstrapSession = createAsyncThunk(
  'session/bootstrap',
  async (companyId: number | undefined, { rejectWithValue }) => {
    try {
      return await sessionApi.init(companyId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
  {
    condition: (_, { getState }) => {
      const { loading } = (getState() as RootState).session;
      return !loading;
    },
  },
);

export const refreshSessionInit = createAsyncThunk(
  'session/refresh',
  async (companyId: number | undefined, { rejectWithValue }) => {
    try {
      return await sessionApi.refresh(companyId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    clearSession: (state) => {
      Object.assign(state, initialState);
    },
    patchOrgStructure: (state, action: { payload: HrOrgStructure }) => {
      state.orgStructure = action.payload;
      if (state.hr) {
        state.hr = {
          ...state.hr,
          departments: action.payload.departments,
          designations: action.payload.designations,
          businessUnits: action.payload.businessUnits,
          grades: action.payload.grades,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(bootstrapSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(bootstrapSession.fulfilled, (state, action) => {
        state.loading = false;
        applySessionPayload(state, action.payload);
      })
      .addCase(bootstrapSession.rejected, (state) => {
        state.loading = false;
        if (state.loaded) {
          return;
        }
        state.loaded = false;
      })
      .addCase(refreshSessionInit.pending, (state) => {
        state.loading = true;
      })
      .addCase(refreshSessionInit.fulfilled, (state, action) => {
        state.loading = false;
        applySessionPayload(state, action.payload);
      })
      .addCase(refreshSessionInit.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { clearSession, patchOrgStructure } = sessionSlice.actions;
export default sessionSlice.reducer;

export function sessionToLoginResponse(data: SessionInitResponse) {
  const menu = data.menu;
  const featureFlags = data.featureFlags;
  return {
    userId: data.userId,
    userUuid: data.userUuid,
    email: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    organizationId: data.organizationId,
    organizationName: data.organizationName,
    organizationUuid: data.organizationUuid,
    storeId: data.storeId,
    roleCode: data.roleCode,
    roleName: data.roleName,
    masterAdmin: data.masterAdmin,
    permissions: data.permissions ?? menu?.permissions ?? [],
    enabledFeatures:
      data.enabledFeatures ?? menu?.enabledFeatures ?? featureFlags?.enabledFeatures ?? [],
    enabledModules:
      data.enabledModules ??
      menu?.enabledModules ??
      (featureFlags?.enabledModules ? [...featureFlags.enabledModules] : []),
    accessibleModules:
      data.accessibleModules ??
      menu?.accessibleModules ??
      (featureFlags?.accessibleModules ? [...featureFlags.accessibleModules] : []),
    sessionUuid: data.sessionUuid,
    mfaEnabled: data.mfaEnabled,
    entryModule: data.entryModule,
    forcePasswordChange: data.forcePasswordChange,
    profilePictureUrl: data.profilePictureUrl,
  };
}
